import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { XIcon, FileTextIcon, AlertCircleIcon, CheckCircleIcon, ShieldIcon, EyeIcon, EyeOffIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
export const TeacherSignUpLayout = () => {
  const navigate = useNavigate();
  const {
    registerTeacher,
    currentUser
  } = useAuth();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    location: ''
  });
  const [verifyChecked, setVerifyChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [signupComplete, setSignupComplete] = useState(false);
  const [redirectCountdown, setRedirectCountdown] = useState(3);
  const [showFirebaseRulesHelp, setShowFirebaseRulesHelp] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    label: 'Too Short',
    color: 'bg-gray-300'
  });
  // Redirect if user is already logged in
  useEffect(() => {
    if (currentUser) {
      // Only redirect if already verified
      if (currentUser.emailVerified) {
        navigate('/teacher-dashboard');
      } else {
        // If logged in but not verified, go to verification page instead
        navigate('/teacher-email-verification');
      }
    }
  }, [currentUser, navigate]);
  // Calculate password strength when password changes
  useEffect(() => {
    setPasswordStrength(calculatePasswordStrength(formData.password));
  }, [formData.password]);
  // Check if passwords match when either password or confirmPassword changes
  useEffect(() => {
    if (formData.confirmPassword && formData.password !== formData.confirmPassword) {
      setPasswordError('Passwords do not match');
    } else {
      setPasswordError('');
    }
  }, [formData.password, formData.confirmPassword]);
  const calculatePasswordStrength = (password: string) => {
    if (!password || password.length < 1) {
      return {
        score: 0,
        label: 'Too Short',
        color: 'bg-gray-300'
      };
    }
    // Calculate base score
    let score = 0;
    // Length check
    if (password.length >= 8) score += 1;
    if (password.length >= 12) score += 1;
    // Character type checks
    if (/[a-z]/.test(password)) score += 0.5;
    if (/[A-Z]/.test(password)) score += 0.5;
    if (/[0-9]/.test(password)) score += 1;
    if (/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+/.test(password)) score += 1;
    // Normalize score to 0-4 range
    score = Math.min(4, score);
    // Map score to label and color
    const strengthMap = [{
      score: 0,
      label: 'Too Short',
      color: 'bg-gray-300'
    }, {
      score: 1,
      label: 'Weak',
      color: 'bg-red-500'
    }, {
      score: 2,
      label: 'Fair',
      color: 'bg-orange-500'
    }, {
      score: 3,
      label: 'Good',
      color: 'bg-yellow-500'
    }, {
      score: 4,
      label: 'Strong',
      color: 'bg-green-500'
    }];
    return strengthMap[Math.floor(score)];
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      id,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [id]: value
    }));
    setErrorMessage('');
  };
  // Handle countdown and redirect
  useEffect(() => {
    if (!signupComplete) return;
    const countdownInterval = setInterval(() => {
      setRedirectCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownInterval);
          console.log('Redirecting to email verification page...');
          // Updated redirect to go to email verification page
          navigate('/teacher-email-verification');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(countdownInterval);
  }, [signupComplete, navigate]);
  const validateForm = () => {
    if (!formData.firstName.trim()) {
      setErrorMessage('Please enter your first name');
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setErrorMessage('Please enter a valid email address');
      return false;
    }
    if (formData.password.length < 8) {
      setErrorMessage('Password must be at least 8 characters long');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setErrorMessage('Passwords do not match');
      return false;
    }
    if (!formData.location.trim()) {
      setErrorMessage('Please enter your location');
      return false;
    }
    if (!verifyChecked) {
      setErrorMessage('Please verify the accuracy of your information');
      return false;
    }
    return true;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');
    setShowFirebaseRulesHelp(false);
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      console.log('Starting teacher registration process...');
      // Generate display name from first and last name
      const displayName = `${formData.firstName} ${formData.lastName}`.trim();
      // Split the location into city and state
      const locationParts = formData.location.split(',');
      const city = locationParts[0]?.trim() || '';
      const state = locationParts.length > 1 ? locationParts[1].trim() : '';
      // Register teacher using Firebase Authentication
      const result = await registerTeacher(formData.email.trim(), formData.password, formData.firstName.trim(), formData.lastName.trim(), formData.location.trim(), city, state);
      console.log('Teacher registration successful!', result);
      setIsSubmitting(false);
      setSuccessMessage('Your teacher account has been created! A verification email has been sent to your inbox.');
      setSignupComplete(true);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        confirmPassword: '',
        location: ''
      });
      setVerifyChecked(false);
      // IMPORTANT: Immediately redirect to email verification page
      // instead of waiting for the countdown timer
      navigate('/teacher-email-verification');
    } catch (error: any) {
      console.error('Signup error:', error);
      // Handle specific Firebase auth errors
      if (error.code === 'auth/email-already-in-use') {
        setErrorMessage('This email is already registered. Please sign in instead.');
      } else if (error.code === 'auth/invalid-email') {
        setErrorMessage('Please enter a valid email address.');
      } else if (error.code === 'auth/weak-password') {
        setErrorMessage('Password is too weak. Please use a stronger password.');
      } else if (error.message && error.message.includes('Firebase permissions error')) {
        // Show special error for Firebase permissions issues
        setErrorMessage('Missing or insufficient permissions.');
        setShowFirebaseRulesHelp(true);
      } else {
        setErrorMessage(error.message || 'An error occurred. Please try again later.');
      }
      setIsSubmitting(false);
    }
  };
  return <div className="w-full" data-id="element-9622">
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-navy-900 relative overflow-hidden" data-id="element-9623">
        <div className="absolute inset-0 z-0" data-id="element-9624">
          {/* Background image with overlay */}
          <div className="absolute inset-0 bg-navy-900/70 z-10" data-id="element-9625"></div>
          <div className="absolute inset-0 bg-cover bg-center" style={{
          backgroundImage: `url('https://uploadthingy.s3.us-west-1.amazonaws.com/s8ktVYbcqPGNW3DKAXWQDj/Piano_teacher_3.jpg')`
        }} data-id="element-9626"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-9627">
          <div className="text-center" data-id="element-9628">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold leading-tight mb-6" data-id="element-9629">
              Start Teaching on{' '}
              <span className="text-deep-pink" data-id="element-9630">MaestroConnect</span>
            </h1>
            <p className="text-xl text-white mb-8 max-w-3xl mx-auto" data-id="element-9631">
              Join a growing network of qualified music teachers and connect
              with students in your area or online.
            </p>
          </div>
        </div>
      </section>
      {/* Form Section */}
      <section className="py-16 bg-navy-900/5" data-id="element-9632">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-9633">
          <div className="bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-100 p-8" data-id="element-9634">
            <h2 className="text-2xl md:text-3xl font-serif font-bold text-navy-900 mb-6 text-center" data-id="element-9635">
              Create Your Teacher Profile
            </h2>
            {/* Success Message */}
            {successMessage && <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-start" data-id="element-9636">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-9637" />
                <div data-id="element-9638">
                  <span data-id="element-9639">{successMessage}</span>
                  {signupComplete && <p className="text-sm mt-1" data-id="element-9640">
                      Redirecting to complete your profile in{' '}
                      {redirectCountdown} seconds...
                    </p>}
                </div>
              </div>}
            {/* Error Message */}
            {errorMessage && <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start" data-id="element-9641">
                <AlertCircleIcon className="h-5 w-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-9642" />
                <span data-id="element-9643">{errorMessage}</span>
              </div>}
            {/* Firebase Rules Help Box */}
            {showFirebaseRulesHelp && <div className="mb-6 bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg" data-id="element-9644">
                <h3 className="font-medium mb-2" data-id="element-9645">
                  Firebase Security Rules Issue
                </h3>
                <p className="mb-2" data-id="element-9646">
                  Your Firebase security rules need to be updated to allow
                  document creation. Update your Firestore rules to:
                </p>
                <pre className="bg-gray-100 p-3 rounded text-xs overflow-x-auto mb-2" data-id="element-9647">
                  {`rules_version = '2';
service cloud.firestore {
  match /databases/{database}/documents {
    // Allow users to create their own documents during registration
    match /users/{userId} {
      allow read: if request.auth != null && request.auth.uid == userId;
      allow create: if request.auth != null && request.auth.uid == userId;
      allow update: if request.auth != null && request.auth.uid == userId;
    }
    // Allow teachers to access and modify their own documents
    match /teachers/{teacherId} {
      allow read: if request.auth != null && request.auth.uid == teacherId;
      allow create: if request.auth != null && request.auth.uid == teacherId;
      allow update: if request.auth != null && request.auth.uid == teacherId;
      // Allow teachers to read and write to their own calendar
      match /calendar/{eventId} {
        allow read, write: if request.auth != null && request.auth.uid == teacherId;
      }
    }
    // Allow students to access and modify their own documents
    match /students/{studentId} {
      allow read: if request.auth != null && request.auth.uid == studentId;
      allow create: if request.auth != null && request.auth.uid == studentId;
      allow update: if request.auth != null && request.auth.uid == studentId;
    }
  }
}`}
                </pre>
                <p className="text-sm" data-id="element-9648">
                  Please update these rules in your Firebase Console under
                  Firestore Database &gt; Rules.
                </p>
              </div>}
            {/* Only show form if signup is not complete */}
            {!signupComplete && <form onSubmit={handleSubmit} data-id="element-9649">
                <div className="space-y-6" data-id="element-9650">
                  {/* First Name and Last Name */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-9651">
                    {/* First Name */}
                    <div data-id="element-9652">
                      <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9653">
                        First Name <span className="text-red-500" data-id="element-9654">*</span>
                      </label>
                      <input type="text" id="firstName" value={formData.firstName} onChange={handleInputChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-deep-pink focus:border-deep-pink text-gray-900" placeholder="Your first name" required disabled={isSubmitting} data-id="element-9655" />
                    </div>
                    {/* Last Name */}
                    <div data-id="element-9656">
                      <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9657">
                        Last Name
                      </label>
                      <input type="text" id="lastName" value={formData.lastName} onChange={handleInputChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-deep-pink focus:border-deep-pink text-gray-900" placeholder="Your last name" disabled={isSubmitting} data-id="element-9658" />
                    </div>
                  </div>
                  {/* Email Address */}
                  <div data-id="element-9659">
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9660">
                      Email Address <span className="text-red-500" data-id="element-9661">*</span>
                    </label>
                    <input type="email" id="email" value={formData.email} onChange={handleInputChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-deep-pink focus:border-deep-pink text-gray-900" placeholder="you@example.com" required disabled={isSubmitting} data-id="element-9662" />
                  </div>
                  {/* Password */}
                  <div data-id="element-9663">
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9664">
                      Password <span className="text-red-500" data-id="element-9665">*</span>
                    </label>
                    <div className="relative" data-id="element-9666">
                      <input type={passwordVisible ? 'text' : 'password'} id="password" value={formData.password} onChange={handleInputChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-deep-pink focus:border-deep-pink text-gray-900" placeholder="Create a secure password (min. 8 characters)" required disabled={isSubmitting} data-id="element-9667" />
                      <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700" onClick={() => setPasswordVisible(!passwordVisible)} disabled={isSubmitting} data-id="element-9668">
                        {passwordVisible ? <EyeOffIcon className="h-5 w-5" data-id="element-9669" /> : <EyeIcon className="h-5 w-5" data-id="element-9670" />}
                      </button>
                    </div>
                    {/* Password Strength Indicator */}
                    <div className="mt-2" data-id="element-9671">
                      <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-9672">
                        <div className={`h-full ${passwordStrength.color} transition-all duration-300`} style={{
                      width: `${passwordStrength.score / 4 * 100}%`
                    }} data-id="element-9673"></div>
                      </div>
                      <div className="mt-1 flex justify-between items-center" data-id="element-9674">
                        <span className={`text-xs font-medium ${passwordStrength.score <= 1 ? 'text-red-500' : passwordStrength.score === 2 ? 'text-orange-500' : passwordStrength.score === 3 ? 'text-yellow-500' : 'text-green-500'}`} data-id="element-9675">
                          {passwordStrength.label}
                        </span>
                        <span className="text-xs text-gray-500" data-id="element-9676">
                          Use 8+ characters, include a number and symbol
                        </span>
                      </div>
                    </div>
                  </div>
                  {/* Confirm Password */}
                  <div data-id="element-9677">
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9678">
                      Confirm Password <span className="text-red-500" data-id="element-9679">*</span>
                    </label>
                    <div className="relative" data-id="element-9680">
                      <input type={confirmPasswordVisible ? 'text' : 'password'} id="confirmPassword" value={formData.confirmPassword} onChange={handleInputChange} className={`w-full px-4 py-3 border rounded-lg focus:ring-deep-pink focus:border-deep-pink text-gray-900 ${passwordError ? 'border-red-500' : 'border-gray-300'}`} placeholder="Confirm your password" required disabled={isSubmitting} data-id="element-9681" />
                      <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700" onClick={() => setConfirmPasswordVisible(!confirmPasswordVisible)} disabled={isSubmitting} data-id="element-9682">
                        {confirmPasswordVisible ? <EyeOffIcon className="h-5 w-5" data-id="element-9683" /> : <EyeIcon className="h-5 w-5" data-id="element-9684" />}
                      </button>
                    </div>
                    {passwordError && <p className="mt-1 text-sm text-red-500" data-id="element-9685">
                        {passwordError}
                      </p>}
                  </div>
                  {/* Location */}
                  <div data-id="element-9686">
                    <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9687">
                      Location (City, State){' '}
                      <span className="text-red-500" data-id="element-9688">*</span>
                    </label>
                    <input type="text" id="location" value={formData.location} onChange={handleInputChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-deep-pink focus:border-deep-pink text-gray-900" placeholder="e.g. Los Angeles, CA" required disabled={isSubmitting} data-id="element-9689" />
                  </div>
                  {/* Identity Verification */}
                  <div className="pt-4 border-t border-gray-200" data-id="element-9690">
                    <h3 className="text-lg font-medium text-gray-900 mb-3" data-id="element-9691">
                      Identity Verification
                    </h3>
                    <div className="bg-blue-50 p-4 rounded-lg mb-4 flex items-start" data-id="element-9692">
                      <ShieldIcon className="h-5 w-5 text-blue-500 mt-0.5 mr-2 flex-shrink-0" data-id="element-9693" />
                      <div className="text-sm text-gray-700" data-id="element-9694">
                        <p className="mb-2" data-id="element-9695">
                          Your privacy and security matter to us. Identity
                          verification is optional for now, but verified
                          teachers appear higher in search results and gain
                          parent trust. Your information is securely processed
                          and used only in accordance with our Privacy Policy.
                        </p>
                      </div>
                    </div>
                  </div>
                  {/* Verification Checkbox */}
                  <div className="flex items-start" data-id="element-9696">
                    <div className="flex items-center h-5" data-id="element-9697">
                      <input id="verify" name="verify" type="checkbox" checked={verifyChecked} onChange={() => setVerifyChecked(!verifyChecked)} className="h-4 w-4 text-deep-pink focus:ring-deep-pink border-gray-300 rounded" required disabled={isSubmitting} data-id="element-9698" />
                    </div>
                    <div className="ml-3 text-sm" data-id="element-9699">
                      <label htmlFor="verify" className="font-medium text-gray-700" data-id="element-9700">
                        I verify the accuracy of my information and agree to the{' '}
                        <a href="#" className="text-deep-pink hover:text-burgundy" data-id="element-9701">
                          Terms of Service
                        </a>
                      </label>
                    </div>
                  </div>
                  {/* Submit Button */}
                  <button type="submit" disabled={isSubmitting || !!passwordError} className={`w-full ${isSubmitting || passwordError ? 'bg-gray-400 cursor-not-allowed' : 'bg-deep-pink hover:bg-burgundy'} text-white font-medium py-3 px-4 rounded-lg transition-colors flex justify-center items-center`} data-id="element-9702">
                    {isSubmitting ? <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-9703">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-9704"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-9705"></path>
                        </svg>
                        Creating Account...
                      </> : 'Create Account – Free 14-Day Trial'}
                  </button>
                  {/* Disclaimer */}
                  <p className="text-sm text-gray-500 text-center mt-4" data-id="element-9706">
                    You won't be charged during your 14-day trial. You can
                    cancel anytime.
                  </p>
                  {/* Sign In Link */}
                  <p className="text-sm text-gray-500 text-center mt-4" data-id="element-9707">
                    Already have an account?{' '}
                    <Link to="/teacher-login" className="text-deep-pink hover:text-burgundy" data-id="element-9708">
                      Sign In
                    </Link>
                  </p>
                </div>
              </form>}
            {/* Success state content */}
            {signupComplete && <div className="text-center py-8" data-id="element-9709">
                <div className="bg-green-100 rounded-full p-4 w-20 h-20 mx-auto mb-6 flex items-center justify-center" data-id="element-9710">
                  <CheckCircleIcon className="h-12 w-12 text-green-500" data-id="element-9711" />
                </div>
                <h3 className="text-xl font-medium text-gray-900 mb-2" data-id="element-9712">
                  Account Created Successfully!
                </h3>
                <p className="text-gray-600 mb-6" data-id="element-9713">
                  A verification email has been sent to your inbox. We're
                  redirecting you in {redirectCountdown} seconds.
                </p>
                <button onClick={() => navigate('/teacher-email-verification')} className="bg-deep-pink hover:bg-burgundy text-white font-medium py-2 px-6 rounded-lg transition-colors" data-id="element-9714">
                  Verify Your Email
                </button>
              </div>}
          </div>
        </div>
      </section>
    </div>;
};